import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { toast } from 'sonner';
import './index.css';
import './brand.css';

import { routeTree } from '@/routeTree.gen';
import '@/translations/config';
import { AuthProvider } from '@/context/AuthContext';
import i18n from 'i18next';
import { config } from './config/config';

const sentryEnv = process.env.CF_PAGES_BRANCH ?? 'local';

if (sentryEnv !== 'local') {
  Sentry.init({
    dsn: 'https://2ec9e83098359cd4d724f36ac2298f3e@o4509038337916928.ingest.de.sentry.io/4511064689016912',
    environment: sentryEnv,
    release: process.env.SENTRY_RELEASE,
    sendDefaultPii: true,
    integrations: [Sentry.replayIntegration()],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      console.error('Query error:', error);
      toast.error(
        `Query Error: ${error instanceof Error ? error.message : 'An error occurred'}`
      );
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      console.error('Mutation error:', error);
      toast.error(
        `Mutation Error: ${error instanceof Error ? error.message : 'An error occurred'}`
      );
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Set initial dir and lang attributes
const initialLang = i18n.language || 'en';
const isRTL = initialLang === 'ar';
document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
document.documentElement.setAttribute('lang', initialLang);


// Auto-reload on stale chunk errors (happens after new deployments)
window.addEventListener('error', (event) => {
  if (event.message?.includes('ChunkLoadError') || event.message?.includes('Loading chunk')) {
    const reloadKey = 'petso_chunk_reload';
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();
    // Only auto-reload once per minute to avoid infinite loops
    if (!lastReload || now - parseInt(lastReload, 10) > 60000) {
      sessionStorage.setItem(reloadKey, String(now));
      window.location.reload();
    }
  }
});

const rootEl = document.getElementById('root');
const clerkPubKey = config.clerk.publishableKey;
if (rootEl) {
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <ClerkProvider publishableKey={clerkPubKey || ''}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
            <ReactQueryDevtools initialIsOpen={false} />
          </AuthProvider>
        </QueryClientProvider>
      </ClerkProvider>
    </React.StrictMode>
  );
}
